<template>
  <div class="dual-sidebar" :class="{ minimized: minimized, mobile: mobile }">
    <!-- LEFT RAIL (always visible) -->
    <div class="sidebar-rail">
      <div class="rail-nav">
        <button
          v-for="(route, index) in navigationRoutes.routes"
          :key="index"
          class="rail-item"
          :class="{ active: selectedIndex === index || routeHasActiveChild(route) }"
          :title="t(route.displayName)"
          @click="handleRailClick(route, index)"
        >
          <VaIcon :name="route.meta.icon" size="22px" class="rail-icon" />
          <span class="rail-label">{{ shortName(route.displayName) }}</span>
        </button>
      </div>

      <div class="rail-footer">
        <button class="rail-toggle-btn" :title="minimized ? 'Expand' : 'Collapse'" @click="$emit('expand')">
          <VaIcon :name="minimized ? 'chevron_right' : 'chevron_left'" size="18px" class="toggle-icon" />
        </button>
      </div>
    </div>

    <!-- RIGHT PANEL (only when not minimized) -->
    <Transition name="panel-slide">
      <div v-if="!minimized" class="sidebar-panel">
        <div v-if="activeRoute" class="panel-body">
          <div class="panel-title">{{ t(activeRoute.displayName) }}</div>

          <template v-if="activeRoute.children">
            <div class="panel-section-label">NAVIGATION</div>
            <RouterLink
              v-for="(child, ci) in activeRoute.children"
              :key="child.name + ci"
              :to="{ name: child.name, query: child.query || {} }"
              class="panel-item"
              :class="{ active: isActiveChildRoute(child) }"
            >
              <VaIcon :name="child.meta.icon" size="16px" class="panel-item-icon" />
              <span class="panel-item-text">{{ t(child.displayName) }}</span>
            </RouterLink>
          </template>
        </div>

        <div v-else class="panel-empty">
          <VaIcon name="apps" size="28px" class="panel-empty-icon" />
          <span>Select a section</span>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script lang="ts">
import { defineComponent, watch, ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useSubscription } from '@/composables/useSubscription'
import navigationRoutes, { type INavigationRoute } from './NavigationRoutes'

export default defineComponent({
  name: 'AppSidebar',
  props: {
    visible: { type: Boolean, default: true },
    mobile: { type: Boolean, default: false },
    minimized: { type: Boolean, default: false },
  },
  emits: ['update:visible', 'expand'],

  setup: (props, { emit }) => {
    const route = useRoute()
    const router = useRouter()
    const { t } = useI18n()
    const { showUpgradePrompt } = useSubscription()

    const selectedIndex = ref<number | null>(null)

    const writableVisible = computed({
      get: () => props.visible,
      set: (v: boolean) => emit('update:visible', v),
    })

    const activeRoute = computed<INavigationRoute | null>(() => {
      if (selectedIndex.value === null) return null
      return navigationRoutes.routes[selectedIndex.value] ?? null
    })

    const isActiveChildRoute = (child: INavigationRoute) => {
      if (route.name !== child.name) return false
      if (child.query) {
        return Object.entries(child.query).every(([k, v]) => route.query[k] === v)
      }
      // For non-query children, active only when no conflicting query is present
      return !Object.keys(route.query).some((k) => activeRoute.value?.children?.some((c) => c.query?.[k] !== undefined))
    }

    const routeHasActiveChild = (section: INavigationRoute): boolean => {
      if (!section.children) {
        if (section.name === 'dashboard') return route.path === '/' || route.name === 'dashboard'
        return route.path.endsWith(`${section.name}`) || route.name === section.name
      }
      return section.children.some(({ name }) => route.path.endsWith(`${name}`) || route.name === name)
    }

    const syncSelectedToRoute = () => {
      const idx = navigationRoutes.routes.findIndex((r) => routeHasActiveChild(r))
      if (idx >= 0) selectedIndex.value = idx
    }

    const shortName = (displayName: string): string => {
      const map: Record<string, string> = {
        'menu.dashboard': 'Dashboard',
        'menu.users': 'Users',
        'menu.developer': 'Developer',
        'menu.documentation': 'Docs',
        'menu.iot-service': 'IoT',
        'menu.services': 'Services',
        'menu.settings': 'Settings',
        'menu.devices': 'Devices',
        'menu.cloud': 'Cloud',
      }
      return map[displayName] ?? displayName.split('.').pop() ?? displayName
    }

    const handleRailClick = (r: INavigationRoute, index: number) => {
      if (props.minimized) {
        if (r.children) {
          emit('expand')
          selectedIndex.value = index
        } else {
          router.push({ name: r.name })
        }
        return
      }
      selectedIndex.value = index
      if (!r.children) {
        router.push({ name: r.name })
      }
    }

    watch(() => route.fullPath, syncSelectedToRoute, { immediate: true })

    return {
      writableVisible,
      selectedIndex,
      activeRoute,
      navigationRoutes,
      routeHasActiveChild,
      isActiveChildRoute,
      handleRailClick,
      shortName,
      showUpgradePrompt,
      t,
    }
  },
})
</script>

<style scoped lang="scss">
/* ─── WRAPPER ───────────────────────────────────────────── */
.dual-sidebar {
  display: flex;
  height: 100%;
  overflow: hidden;
}

/* ─── LEFT RAIL ─────────────────────────────────────────── */
.sidebar-rail {
  width: 64px;
  min-width: 64px;
  display: flex;
  flex-direction: column;
  background: var(--va-background-primary);
  border-right: 1px solid var(--nx-glass-border);
}

.rail-nav {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 4px 0;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.rail-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 10px 4px;
  border: none;
  background: transparent;
  cursor: pointer;
  gap: 4px;
  position: relative;
  transition: background 0.15s;

  /* left accent bar — hidden by default */
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 18%;
    height: 64%;
    width: 2.5px;
    background: var(--va-primary);
    border-radius: 0 2px 2px 0;
    opacity: 0;
    transform: scaleY(0.5);
    transition:
      opacity 0.2s,
      transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  &:hover {
    background: var(--va-background-element);

    .rail-icon {
      color: var(--va-text-primary) !important;
    }

    .rail-label {
      opacity: 1;
    }
  }

  &.active {
    background: var(--va-background-element);

    &::before {
      opacity: 1;
      transform: scaleY(1);
    }

    .rail-icon {
      color: var(--va-primary) !important;
    }

    .rail-label {
      color: var(--va-primary);
      opacity: 1;
      font-weight: 700;
    }
  }
}

.rail-icon {
  color: var(--va-text-secondary) !important;
  transition:
    color 0.15s,
    transform 0.15s;
  flex-shrink: 0;
}

.rail-label {
  font-size: 9.5px;
  color: var(--va-text-secondary);
  font-weight: 500;
  text-align: center;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 58px;
  opacity: 0.7;
  transition:
    color 0.15s,
    opacity 0.15s;
}

/* ─── RAIL FOOTER ───────────────────────────────────────── */
.rail-footer {
  padding: 10px 0;
  display: flex;
  justify-content: center;
  border-top: 1px solid var(--nx-glass-border);
}

.rail-toggle-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 5px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;

  &:hover {
    background: var(--va-background-element);
  }
}

.toggle-icon {
  color: var(--va-text-secondary) !important;
}

/* ─── RIGHT PANEL ───────────────────────────────────────── */
.sidebar-panel {
  width: 196px;
  min-width: 196px;
  display: flex;
  flex-direction: column;
  background: var(--va-background-secondary);
  border-right: 1px solid var(--nx-glass-border);
  overflow: hidden;
}

.panel-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 0 8px;
  scrollbar-width: thin;
  scrollbar-color: var(--va-background-element) transparent;
}

.panel-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--va-text-primary);
  padding: 0 14px 14px;
  letter-spacing: 0.01em;
  text-transform: uppercase;
}

.panel-section-label {
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: var(--va-primary);
  padding: 4px 14px 6px;
  text-transform: uppercase;
}

.panel-item {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 8px 14px;
  text-decoration: none;
  color: var(--va-text-secondary);
  font-size: 12.5px;
  font-weight: 500;
  transition:
    background 0.15s,
    color 0.15s;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 15%;
    height: 70%;
    width: 2px;
    background: var(--va-primary);
    border-radius: 0 2px 2px 0;
    opacity: 0;
    transform: scaleY(0);
    transition:
      opacity 0.15s,
      transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  &:hover {
    background: var(--va-background-element);
    color: var(--va-text-primary);
  }

  &.active {
    background: var(--va-background-element);
    color: var(--va-primary);
    font-weight: 600;

    &::before {
      opacity: 1;
      transform: scaleY(1);
    }

    .panel-item-icon {
      color: var(--va-primary) !important;
    }
  }
}

.panel-item-icon {
  flex-shrink: 0;
  opacity: 0.7;
  transition: color 0.15s;
}

.panel-item-text {
  line-height: 1.3;
}

.panel-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 11px;
  color: var(--va-text-secondary);
  opacity: 0.35;
}

.panel-empty-icon {
  color: var(--va-text-secondary) !important;
}

/* ─── MINIMIZED ─────────────────────────────────────────── */
.dual-sidebar.minimized .sidebar-panel {
  display: none;
}

/* ─── MOBILE ────────────────────────────────────────────── */
.dual-sidebar.mobile {
  width: 100vw;

  .sidebar-panel {
    flex: 1;
  }
}

/* ─── PANEL SLIDE TRANSITION ────────────────────────────── */
.panel-slide-enter-active,
.panel-slide-leave-active {
  transition:
    width 0.25s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.2s ease;
}

.panel-slide-enter-from,
.panel-slide-leave-to {
  width: 0 !important;
  min-width: 0 !important;
  opacity: 0;
}
</style>
